import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Meaning, Language } from '../types/meaning';
import { getMeanings, createMeaning, updateMeaning, deleteMeaning } from '../services/meanings';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    CircularProgress,
    Alert,
    Typography,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useConfirm } from '../contexts/ConfirmDialogContext';

const MeaningsList: React.FC = () => {
    const { projectName, section, chapter, sutraNumber } = useParams<{ projectName: string; section: string; chapter: string; sutraNumber: string }>();
    const [meanings, setMeanings] = useState<Meaning[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingMeaning, setEditingMeaning] = useState<Meaning | null>(null);
    const [formData, setFormData] = useState<Omit<Meaning, 'id'>>({
        language: Language.ENGLISH,
        text: '',
    });
    const navigate = useNavigate();
    const confirm = useConfirm();

    const fetchMeanings = useCallback(async () => {
        if (!projectName || !section || !chapter || !sutraNumber) return;
        try {
            setLoading(true);
            setError(null);
            const data = await getMeanings(projectName, section, parseInt(chapter), parseInt(sutraNumber));
            setMeanings(data);
        } catch (err) {
            console.error('Error fetching meanings:', err);
            setError('Failed to fetch meanings');
            setMeanings([]);
        } finally {
            setLoading(false);
        }
    }, [projectName, section, chapter, sutraNumber]);

    useEffect(() => {
        fetchMeanings();
    }, [fetchMeanings]);

    const handleOpenDialog = (meaning?: Meaning) => {
        if (meaning) {
            setEditingMeaning(meaning);
            setFormData({
                language: meaning.language,
                text: meaning.text,
            });
        } else {
            setEditingMeaning(null);
            setFormData({
                language: Language.ENGLISH,
                text: '',
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingMeaning(null);
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            setError(null);
            if (editingMeaning) {
                await updateMeaning(projectName!, section!, parseInt(chapter!), parseInt(sutraNumber!), editingMeaning.language, {
                    ...editingMeaning,
                    ...formData,
                });
            } else {
                await createMeaning(projectName!, section!, parseInt(chapter!), parseInt(sutraNumber!), formData);
            }
            handleCloseDialog();
            fetchMeanings();
        } catch (err) {
            console.error('Error saving meaning:', err);
            setError('Failed to save meaning');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (language: Language) => {
        const ok = await confirm({
            title: 'Confirm Delete',
            message: `Are you sure you want to delete ...?`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
        });

        if (!ok) return;

        try {
            setLoading(true);
            setError(null);
            await deleteMeaning(projectName!, section!, parseInt(chapter!), parseInt(sutraNumber!), language);
            fetchMeanings();
        } catch (err) {
            console.error('Error deleting meaning:', err);
            setError('Failed to delete meaning');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Typography variant="h5" gutterBottom>
                Meanings for Sutra {section}/{chapter}/{sutraNumber} in {projectName}
            </Typography>

            <Button variant="contained" color="primary" onClick={() => handleOpenDialog()} sx={{ mb: 2 }}>
                Add New Meaning
            </Button>

            {loading && <CircularProgress sx={{ mt: 2 }} />}

            {error && (
                <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {!loading && meanings.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    No meanings found for this sutra.
                </Alert>
            )}

            {meanings.length > 0 && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Language</TableCell>
                                <TableCell>Text</TableCell>
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {meanings.map((meaning) => (
                                <TableRow key={meaning.id}>
                                    <TableCell>{meaning.language}</TableCell>
                                    <TableCell>{meaning.text}</TableCell>
                                    <TableCell>
                                        <IconButton onClick={() => handleOpenDialog(meaning)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton onClick={() => handleDelete(meaning.language as Language)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>{editingMeaning ? 'Edit Meaning' : 'Add New Meaning'}</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth margin="dense">
                        <InputLabel>Language</InputLabel>
                        <Select
                            value={formData.language}
                            label="Language"
                            onChange={(e) => setFormData({ ...formData, language: e.target.value as Language })}
                        >
                            {Object.values(Language).map((lang) => (
                                <MenuItem key={lang} value={lang}>
                                    {lang.toUpperCase()}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        margin="dense"
                        label="Text"
                        fullWidth
                        multiline
                        rows={4}
                        value={formData.text}
                        onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button onClick={handleSubmit} color="primary" disabled={loading}>
                        {editingMeaning ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default MeaningsList; 